import { FC } from 'react';
import { Empty } from 'antd';
import { Container } from '../../components/container/container';
import { ListingCard } from '../../components/cards/listing-card/listing-card';
import { Catalog } from '../../apis/search-catalog';
import striptags from 'striptags';
import { SearchBar, Button } from '@plataforma-xvia/design-system-es';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../routes';
import {
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
} from '../../icons/CheckboxIcon';

import {
  CategoryFilter,
  CategoryFilter as DepartmentFilter,
} from './list.container';
import { Pagination } from '../../components';

import styles from './list.module.css';
import { IDepartment } from '../../apis/department';
import { DriveFileRenameOutline, FilterAltOutlined } from '@mui/icons-material';

export interface ListProps {
  list?: Catalog[];
  onClick: (catalog: Catalog) => void;
  onClickFavourite: (slug: string) => void;
  onClickOnlineService: (link: string) => void;
  page: number;
  handleChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  toggleDrawer: (newOpen: boolean) => () => void;
  selectedCategories: CategoryFilter[];
  appliedCategories: CategoryFilter[];
  appliedDepartments: DepartmentFilter[];
  categories: { id: string; name: string; slug: string }[];
  departments: IDepartment[];
  selectedDepartments: DepartmentFilter[];
  handleCheckboxChange: (filter: CategoryFilter) => void;
  handleDepartmentCheckboxChange: (filter: DepartmentFilter) => void;
  handleFilterByCategory: () => void;
  open: boolean;
  resultTotal?: number;
  totalPages: number;
  disposition: 'line' | 'card';
  removeCategoryFilter: (slug: string) => void;
  removeDepartmentFilter: (slug: string) => void;
  onChangeText: (text: string) => void;
  value: string;
  activeTab: 'all' | 'online' | 'top10';
  onTabChange: (tab: 'all' | 'online' | 'top10') => void;
  top10List?: Catalog[];
  clearCategoriesFilter: () => void;
  clearDepartmentsFilter: () => void;
}

export const List: FC<ListProps> = ({
  list,
  onClick,
  onClickFavourite,
  onClickOnlineService,
  page,
  handleChange,
  toggleDrawer,
  selectedCategories,
  appliedCategories,
  categories,
  departments,
  selectedDepartments,
  appliedDepartments,
  handleCheckboxChange,
  handleDepartmentCheckboxChange,
  handleFilterByCategory,
  open,
  disposition,
  resultTotal,
  totalPages,
  removeCategoryFilter,
  removeDepartmentFilter,
  onChangeText,
  value,
  activeTab,
  onTabChange,
  top10List,
  clearCategoriesFilter,
  clearDepartmentsFilter,
}) => {
  const history = useHistory();

  const handleRemoveFilter = (filter: {
    id: string;
    type: 'cat' | 'dep';
    name: string;
    slug: string;
  }) => {
    if (filter.type === 'cat') {
      removeCategoryFilter(filter.slug);
    } else {
      removeDepartmentFilter(filter.slug);
    }
  };

  const FilterDialog = (
    <Dialog
      open={open}
      onClose={toggleDrawer(false)}
      maxWidth="md"
      fullWidth
      aria-labelledby="filter-dialog-title"
      className={styles.filterDialog}
    >
      <DialogTitle className={styles.filterHeader}>
        <Box component="span" className={styles.headerTitle}>
          <FilterAltOutlined className={styles.filterIcon} />
          Filtrar por
        </Box>
        <IconButton
          aria-label="fechar"
          onClick={toggleDrawer(false)}
          className={styles.closeBtn}
          size="small"
          sx={{ color: '#EAF0F4' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={styles.dialogContent}>
        <Box component="section" className={styles.section}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <h3 className={styles.sectionTitle}>Categoria</h3>
            <Button
              className={styles.filterButtonClearAll}
              onClick={clearCategoriesFilter}
              aria-label="Limpar todas as categorias"
              variant="text"
              size="none"
              label="Limpar categorias"
              type="button"
            />
          </Box>

          <div className={styles.categoryGrid}>
            {categories?.map(({ id, name, slug }) => (
              <FormControlLabel
                key={id}
                className={styles.listFilterDialogLabel}
                style={{ fontSize: '0.750rem !important' }}
                control={
                  <Checkbox
                    checked={selectedCategories.some((c) => c.id === id)}
                    onChange={() => handleCheckboxChange({ id, name, slug })}
                    size="small"
                    icon={<CheckboxUncheckedIcon />}
                    checkedIcon={<CheckboxCheckedIcon />}
                  />
                }
                label={name}
              />
            ))}
          </div>
        </Box>

        <Box component="section" className={styles.section}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <h3 className={styles.sectionTitle}>Órgãos e Secretarias</h3>
            <Button
              className={styles.filterButtonClearAll}
              onClick={clearDepartmentsFilter}
              aria-label="Limpar todos os órgãos"
              variant="text"
              size="none"
              label="Limpar órgãos"
              type="button"
            />
          </Box>
          <div className={styles.categoryGrid}>
            {departments?.map(({ id, name, slug }) => (
              <FormControlLabel
                key={id}
                className={styles.listFilterDialogLabel}
                control={
                  <Checkbox
                    checked={selectedDepartments.some((d) => d.id === id)}
                    onChange={() =>
                      handleDepartmentCheckboxChange({ id, name, slug })
                    }
                    size="small"
                    icon={<CheckboxUncheckedIcon />}
                    checkedIcon={<CheckboxCheckedIcon />}
                  />
                }
                label={departments.find((d) => d.id === id)?.shortName ?? name}
              />
            ))}
          </div>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }} className={styles.filterButtonApply}>
        <Button
          onClick={handleFilterByCategory}
          variant="solid"
          tone="primary"
          size="md"
          label="Preencher"
          iconStart={<DriveFileRenameOutline fontSize="small" />}
        />
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {FilterDialog}

      <div className={styles.listContainer}>
        <Container>
          <div className={styles.searchContainer}>
            <SearchBar
              onClickSearch={toggleDrawer(true)}
              onChangeText={onChangeText}
              value={value}
              placeholder="Buscar..."
            />
          </div>

          {appliedCategories.length + appliedDepartments.length > 0 &&
            (() => {
              const chips: {
                id: string;
                type: 'cat' | 'dep';
                name: string;
                slug: string;
              }[] = [
                ...appliedCategories.map((c) => ({
                  ...c,
                  type: 'cat' as const,
                  slug: c.slug || '',
                })),
                ...appliedDepartments.map((d) => ({
                  ...d,
                  type: 'dep' as const,
                  slug: d.slug || '',
                })),
              ];
              return (
                <div className={styles.filtersSection}>
                  <span className={styles.filtersTitle}>Filtros aplicados</span>
                  <div className={styles.selectedFilters}>
                    {chips.map((filter) => {
                      const filterLabel =
                        filter.type === 'cat'
                          ? filter.name
                          : departments.find((d) => d.id === filter.id)
                              ?.shortName ?? filter.name;

                      return (
                        <Button
                          key={`${filter.type}-${filter.id}`}
                          className={styles.filterChip}
                          onClick={() => handleRemoveFilter(filter)}
                          title="Remover filtro"
                          aria-label={`Remover filtro ${filterLabel}`}
                          variant="text"
                          size="none"
                          iconStart={
                            <CloseIcon
                              className={styles.chipClose}
                              fontSize="inherit"
                            />
                          }
                          label={filterLabel || ''}
                          type="button"
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          <div className={styles.headerContainer}>
            <div className={styles.tabGroup}>
              <Button
                className={`${styles.tab} ${
                  activeTab === 'all' ? styles.active : ''
                }`}
                onClick={() => onTabChange('all')}
                variant="text"
                size="none"
                type="button"
                aria-pressed={activeTab === 'all'}
                label="Todos"
              />
              <Button
                className={`${styles.tab} ${
                  activeTab === 'online' ? styles.active : ''
                }`}
                onClick={() => onTabChange('online')}
                variant="text"
                size="none"
                type="button"
                aria-pressed={activeTab === 'online'}
                label="Online"
              />
              <Button
                className={`${styles.tab} ${
                  activeTab === 'top10' ? styles.active : ''
                }`}
                onClick={() => onTabChange('top10')}
                variant="text"
                size="none"
                type="button"
                aria-pressed={activeTab === 'top10'}
                label="Top 10 serviços"
              />
            </div>
          </div>

          <div className={styles.gridWrapper}>
            <div className={styles.grid} data-testid="listing-card-container">
              {activeTab === 'top10'
                ? top10List?.map((catalog) => {
                    const href = history.createHref({
                      pathname: `${ROUTES.SERVICE_LIST_PAGE}/${catalog.slug}`,
                    });
                    return (
                      <div className={styles.card} key={catalog.slug}>
                        <ListingCard
                          title={catalog.title}
                          category={catalog.category || catalog.categorySlug?.replace(/-/g, ' ')}
                          subtitle={striptags(catalog.description)}
                          type={catalog.isOnline ? 'online' : 'in-person'}
                          rating={catalog.avgRating ? parseFloat(String(catalog.avgRating)) : catalog.averageRatings?.average}
                          onClick={() => onClick(catalog)}
                          onClickFavourite={() => onClickFavourite(catalog.slug)}
                          onClickOnlineService={() =>
                            catalog.link
                              ? onClickOnlineService(catalog.link)
                              : onClick(catalog)
                          }
                          disposition={disposition}
                          isDigital={catalog.isDigital || catalog.isOnline}
                          department={catalog.department}
                          href={href}
                          suspension={catalog.suspension}
                        />
                      </div>
                    );
                  })
                : list?.map((catalog) => {
                    const href = history.createHref({
                      pathname: `${ROUTES.SERVICE_LIST_PAGE}/${catalog.slug}`,
                    });
                    return (
                      <div className={styles.card} key={catalog.title}>
                        <ListingCard
                          title={catalog.title}
                          category={catalog.category}
                          subtitle={striptags(catalog.description)}
                          type={catalog.isOnline ? 'online' : 'in-person'}
                          onClick={() => onClick(catalog)}
                          onClickFavourite={() => onClickFavourite(catalog.slug)}
                          onClickOnlineService={() =>
                            onClickOnlineService(catalog.link)
                          }
                          disposition={disposition}
                          isDigital={catalog.isDigital}
                          department={catalog.department}
                          href={href}
                          suspension={catalog.suspension}
                        />
                      </div>
                    );
                  })}
            </div>

            {activeTab === 'top10' && !top10List?.length && (
              <Empty description="Nenhum serviço encontrado" />
            )}
            {activeTab !== 'top10' && !list?.length && (
              <Empty description="Nenhum serviço encontrado" />
            )}

            {activeTab !== 'top10' && list && list.length > 0 ? (
              <Pagination
                count={totalPages}
                onChange={handleChange}
                page={page}
              />
            ) : activeTab !== 'top10' ? (
              <div style={{ height: '5em' }} />
            ) : null}
          </div>
        </Container>
      </div>
    </>
  );
};
